import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Plug, 
  CheckCircle, 
  AlertTriangle, 
  Settings, 
  RefreshCw,
  DollarSign,
  FileText,
  Database,
  Mail,
  Shield,
  Zap,
  Plus,
  ExternalLink,
  Clock,
  Users
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Integration {
  id: string;
  name: string;
  category: 'accounting' | 'payment' | 'crm' | 'email' | 'analytics' | 'security';
  description: string;
  status: 'connected' | 'disconnected' | 'error' | 'syncing';
  lastSync?: string;
  features: string[];
  isPopular?: boolean;
  logo: React.ComponentType<{ className?: string }>;
  credentials?: {
    apiKey?: string;
    webhook?: string;
    accountId?: string;
  };
}

interface ApiEndpoint {
  id: string;
  name: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  endpoint: string;
  description: string;
  status: 'active' | 'inactive';
  requestsCount: number;
  lastUsed: string;
}

export const ThirdPartyIntegrations = () => {
  const { toast } = useToast();
  const [showConnectionDialog, setShowConnectionDialog] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [syncingIntegrations, setSyncingIntegrations] = useState<string[]>([]);

  const integrations: Integration[] = [
    {
      id: 'quickbooks',
      name: 'QuickBooks Online',
      category: 'accounting',
      description: 'Sync bills and expenses with your QuickBooks accounting system',
      status: 'connected',
      lastSync: '2024-01-23T10:30:00Z',
      features: ['Automatic expense categorization', 'Real-time sync', 'Tax reporting'],
      isPopular: true,
      logo: FileText,
      credentials: {
        accountId: 'qb_acc_123456'
      }
    },
    {
      id: 'stripe',
      name: 'Stripe',
      category: 'payment',
      description: 'Process payments and manage billing through Stripe',
      status: 'connected',
      lastSync: '2024-01-23T09:15:00Z',
      features: ['Payment processing', 'Subscription management', 'Financial reporting'],
      isPopular: true,
      logo: DollarSign,
      credentials: {
        apiKey: 'sk_live_****************************'
      }
    },
    {
      id: 'xero',
      name: 'Xero',
      category: 'accounting',
      description: 'Connect with Xero for seamless accounting integration',
      status: 'disconnected',
      features: ['Bill management', 'Financial reporting', 'Tax compliance'],
      isPopular: true,
      logo: Database
    },
    {
      id: 'mailchimp',
      name: 'Mailchimp',
      category: 'email',
      description: 'Send automated billing notifications and reports',
      status: 'connected',
      lastSync: '2024-01-23T08:45:00Z',
      features: ['Email campaigns', 'Automated notifications', 'Analytics'],
      logo: Mail,
      credentials: {
        apiKey: 'mc_api_****************************'
      }
    },
    {
      id: 'salesforce',
      name: 'Salesforce',
      category: 'crm',
      description: 'Sync customer data and billing information with Salesforce CRM',
      status: 'error',
      features: ['Customer sync', 'Opportunity tracking', 'Report integration'],
      isPopular: true,
      logo: Users
    },
    {
      id: 'zapier',
      name: 'Zapier',
      category: 'analytics',
      description: 'Connect with 1000+ apps through Zapier automation',
      status: 'disconnected',
      features: ['Workflow automation', 'Data synchronization', 'Custom triggers'],
      logo: Zap
    },
    {
      id: 'okta',
      name: 'Okta',
      category: 'security',
      description: 'Enterprise identity and access management',
      status: 'disconnected',
      features: ['Single sign-on', 'Multi-factor auth', 'User management'],
      logo: Shield
    }
  ];

  const apiEndpoints: ApiEndpoint[] = [
    {
      id: 'bills-api',
      name: 'Bills API',
      method: 'GET',
      endpoint: '/api/v1/bills',
      description: 'Retrieve bill data for external systems',
      status: 'active',
      requestsCount: 1247,
      lastUsed: '2024-01-23T11:30:00Z'
    },
    {
      id: 'webhook-bills',
      name: 'Bill Webhook',
      method: 'POST',
      endpoint: '/api/v1/webhooks/bills',
      description: 'Receive real-time bill processing updates',
      status: 'active',
      requestsCount: 892,
      lastUsed: '2024-01-23T11:15:00Z'
    },
    {
      id: 'analytics-api',
      name: 'Analytics API',
      method: 'GET',
      endpoint: '/api/v1/analytics',
      description: 'Access processed analytics and insights',
      status: 'active',
      requestsCount: 456,
      lastUsed: '2024-01-23T10:45:00Z'
    },
    {
      id: 'export-api',
      name: 'Export API',
      method: 'POST',
      endpoint: '/api/v1/export',
      description: 'Export data in various formats',
      status: 'inactive',
      requestsCount: 23,
      lastUsed: '2024-01-20T14:20:00Z'
    }
  ];

  const handleConnect = async (integration: Integration) => {
    setSelectedIntegration(integration);
    setShowConnectionDialog(true);
  };

  const handleConnectionSubmit = async () => {
    setIsConnecting(true);
    
    // Simulate connection process
    setTimeout(() => {
      setIsConnecting(false);
      setShowConnectionDialog(false);
      
      if (selectedIntegration) {
        // Update integration status
        const index = integrations.findIndex(i => i.id === selectedIntegration.id);
        if (index !== -1) {
          integrations[index].status = 'connected';
          integrations[index].lastSync = new Date().toISOString();
        }
        
        toast({
          title: "Integration Connected",
          description: `Successfully connected to ${selectedIntegration.name}`,
        });
      }
    }, 2000);
  };

  const handleDisconnect = (integration: Integration) => {
    const index = integrations.findIndex(i => i.id === integration.id);
    if (index !== -1) {
      integrations[index].status = 'disconnected';
      integrations[index].lastSync = undefined;
    }
    
    toast({
      title: "Integration Disconnected",
      description: `Disconnected from ${integration.name}`,
      variant: "destructive",
    });
  };

  const handleSync = async (integration: Integration) => {
    setSyncingIntegrations(prev => [...prev, integration.id]);
    
    // Simulate sync process
    setTimeout(() => {
      setSyncingIntegrations(prev => prev.filter(id => id !== integration.id));
      
      toast({
        title: "Sync Complete",
        description: `Data synchronized with ${integration.name}`,
      });
    }, 3000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-300';
      case 'syncing': return 'text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-300';
      case 'error': return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-300';
      case 'disconnected': return 'text-gray-600 bg-gray-100 dark:bg-gray-900 dark:text-gray-300';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected': return <CheckCircle className="h-4 w-4" />;
      case 'syncing': return <RefreshCw className="h-4 w-4 animate-spin" />;
      case 'error': return <AlertTriangle className="h-4 w-4" />;
      default: return <Plug className="h-4 w-4" />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'accounting': return <FileText className="h-5 w-5" />;
      case 'payment': return <DollarSign className="h-5 w-5" />;
      case 'crm': return <Users className="h-5 w-5" />;
      case 'email': return <Mail className="h-5 w-5" />;
      case 'analytics': return <Zap className="h-5 w-5" />;
      case 'security': return <Shield className="h-5 w-5" />;
      default: return <Plug className="h-5 w-5" />;
    }
  };

  const formatLastSync = (lastSync?: string) => {
    if (!lastSync) return 'Never';
    const date = new Date(lastSync);
    return date.toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Plug className="h-6 w-6 text-primary" />
            Third-Party Integrations
          </h2>
          <p className="text-muted-foreground">
            Connect with external services to streamline your workflow
          </p>
        </div>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Browse Integrations
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Integrations</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {integrations.filter(i => i.status === 'connected').length}
            </div>
            <p className="text-xs text-muted-foreground">
              Out of {integrations.length} available
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">API Requests</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">2.6K</div>
            <p className="text-xs text-muted-foreground">
              This month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Sync</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">2m ago</div>
            <p className="text-xs text-muted-foreground">
              QuickBooks Online
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Error Rate</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">0.3%</div>
            <p className="text-xs text-muted-foreground">
              Last 24 hours
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="integrations" className="space-y-4">
        <TabsList>
          <TabsTrigger value="integrations">Available Integrations</TabsTrigger>
          <TabsTrigger value="api">API Endpoints</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="logs">Activity Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="integrations" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {integrations.map((integration) => (
              <Card key={integration.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <integration.logo className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          {integration.name}
                          {integration.isPopular && (
                            <Badge variant="secondary" className="text-xs">Popular</Badge>
                          )}
                        </CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          {getCategoryIcon(integration.category)}
                          <span className="text-sm text-muted-foreground capitalize">
                            {integration.category}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Badge className={getStatusColor(integration.status)}>
                      <div className="flex items-center gap-1">
                        {getStatusIcon(integration.status)}
                        {integration.status}
                      </div>
                    </Badge>
                  </div>
                  <CardDescription>{integration.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Features</div>
                    <div className="flex flex-wrap gap-1">
                      {integration.features.map((feature, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {feature}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {integration.lastSync && (
                    <div className="text-sm text-muted-foreground">
                      Last sync: {formatLastSync(integration.lastSync)}
                    </div>
                  )}

                  <div className="flex gap-2">
                    {integration.status === 'connected' ? (
                      <>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => handleSync(integration)}
                          disabled={syncingIntegrations.includes(integration.id)}
                          className="flex-1"
                        >
                          <RefreshCw className={`h-4 w-4 mr-2 ${syncingIntegrations.includes(integration.id) ? 'animate-spin' : ''}`} />
                          Sync
                        </Button>
                        <Button size="sm" variant="outline">
                          <Settings className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleDisconnect(integration)}
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <Button 
                        size="sm" 
                        onClick={() => handleConnect(integration)}
                        className="flex-1"
                      >
                        <Plug className="h-4 w-4 mr-2" />
                        Connect
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="api" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>API Endpoints</CardTitle>
              <CardDescription>
                Available REST API endpoints for custom integrations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {apiEndpoints.map((endpoint) => (
                  <div key={endpoint.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <Badge variant={endpoint.method === 'GET' ? 'default' : 'secondary'}>
                          {endpoint.method}
                        </Badge>
                        <code className="text-sm bg-muted px-2 py-1 rounded">
                          {endpoint.endpoint}
                        </code>
                        <Badge variant={endpoint.status === 'active' ? 'default' : 'secondary'}>
                          {endpoint.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{endpoint.description}</p>
                      <div className="text-xs text-muted-foreground">
                        {endpoint.requestsCount} requests • Last used: {formatLastSync(endpoint.lastUsed)}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline">
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                      <Switch checked={endpoint.status === 'active'} />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="webhooks" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Webhook Configuration</CardTitle>
              <CardDescription>
                Set up webhooks to receive real-time updates
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="webhook-url">Webhook URL</Label>
                  <Input id="webhook-url" placeholder="https://your-app.com/webhook" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="webhook-secret">Secret Key</Label>
                  <Input id="webhook-secret" type="password" placeholder="webhook_secret_key" />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Events to Subscribe</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    'bill.processed',
                    'bill.failed',
                    'anomaly.detected',
                    'sync.completed',
                    'user.created',
                    'report.generated'
                  ].map((event) => (
                    <div key={event} className="flex items-center space-x-2">
                      <Switch id={event} />
                      <Label htmlFor={event} className="text-sm">{event}</Label>
                    </div>
                  ))}
                </div>
              </div>
              
              <Button>Save Webhook Configuration</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Integration Activity Logs</CardTitle>
              <CardDescription>
                Recent integration activity and system events
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { time: '11:30 AM', service: 'QuickBooks', action: 'Data sync completed', status: 'success' },
                  { time: '11:15 AM', service: 'Stripe', action: 'Payment webhook received', status: 'success' },
                  { time: '10:45 AM', service: 'Mailchimp', action: 'Campaign sent successfully', status: 'success' },
                  { time: '10:30 AM', service: 'Salesforce', action: 'Connection failed - timeout', status: 'error' },
                  { time: '09:15 AM', service: 'Zapier', action: 'Workflow triggered', status: 'success' },
                ].map((log, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border-l-4 border-l-primary/20">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{log.service}</span>
                        <Badge variant={log.status === 'success' ? 'default' : 'destructive'}>
                          {log.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{log.action}</p>
                    </div>
                    <div className="text-sm text-muted-foreground">{log.time}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Connection Dialog */}
      <Dialog open={showConnectionDialog} onOpenChange={setShowConnectionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Connect to {selectedIntegration?.name}</DialogTitle>
            <DialogDescription>
              Enter your credentials to connect with {selectedIntegration?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="api-key">API Key</Label>
              <Input id="api-key" placeholder="Enter your API key" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="account-id">Account ID (Optional)</Label>
              <Input id="account-id" placeholder="Enter account ID if required" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConnectionDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConnectionSubmit} disabled={isConnecting}>
              {isConnecting ? 'Connecting...' : 'Connect'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};